import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["button", "dropdown", "placeholder", "checkbox", "chips"];
  static values = { renderUrl: String };

  connect() {
    this.updatePlaceholder();
    // Initial chips are now rendered server-side, so we don't need to render them here
    // They will only be re-rendered when checkboxes change

    // Close dropdown when clicking outside
    this._outsideClickHandler = this.handleOutsideClick.bind(this);
    document.addEventListener("click", this._outsideClickHandler);

    // Handle keyboard events (Escape to close, Enter to open, focus mgmt)
    this._keydownHandler = this.handleKeydown.bind(this);
    document.addEventListener("keydown", this._keydownHandler);

    // Ensure initial ARIA state
    if (this.hasButtonTarget)
      this.buttonTarget.setAttribute("aria-expanded", "false");
  }

  disconnect() {
    document.removeEventListener("click", this._outsideClickHandler);
    document.removeEventListener("keydown", this._keydownHandler);
  }

  toggle(event) {
    event.preventDefault();
    event.stopPropagation();

    const willOpen = this.dropdownTarget.classList.contains("hidden");
    this.dropdownTarget.classList.toggle("hidden");

    // Update aria-expanded
    if (this.hasButtonTarget)
      this.buttonTarget.setAttribute(
        "aria-expanded",
        willOpen ? "true" : "false",
      );

    // Move focus to first checkbox when opening
    if (willOpen && this.checkboxTargets.length > 0) {
      try {
        this.checkboxTargets[0].focus();
      } catch (_) {}
    }
  }

  checkboxChanged() {
    this.updatePlaceholder();
    // Render chips if chips target is present (for context tags)
    if (this.hasChipsTarget) {
      if (this.hasRenderUrlValue) {
        this.fetchRenderedChips();
      } else {
        this.renderChips();
      }
    }
  }

  closeDropdown() {
    if (!this.dropdownTarget.classList.contains("hidden")) {
      this.dropdownTarget.classList.add("hidden");
      if (this.hasButtonTarget) {
        // Return focus to trigger and update ARIA state
        try {
          this.buttonTarget.focus();
        } catch (_) {}
        this.buttonTarget.setAttribute("aria-expanded", "false");
      }
    }
  }

  updatePlaceholder() {
    const checkedBoxes = this.checkboxTargets.filter(
      (checkbox) => checkbox.checked,
    );
    const checkedNames = checkedBoxes.map(
      (checkbox) =>
        checkbox.dataset.tagName || checkbox.dataset.userName || checkbox.value,
    );

    if (checkedNames.length === 0) {
      this.placeholderTarget.textContent = this.getPlaceholderText("empty");
      this.placeholderTarget.classList.add("text-neutral-500");
    } else if (checkedNames.length === 1) {
      this.placeholderTarget.textContent = checkedNames[0];
      this.placeholderTarget.classList.remove("text-neutral-500");
    } else {
      this.placeholderTarget.textContent = `${checkedNames.length} selected`;
      this.placeholderTarget.classList.remove("text-neutral-500");
    }
  }

  getPlaceholderText(state) {
    // Check if there's a custom placeholder attribute, otherwise use defaults
    if (state === "empty") {
      return this.element.dataset.placeholder || "Select cast";
    }
    return "";
  }

  async fetchRenderedChips() {
    const selected = this.checkboxTargets.filter((cb) => cb.checked);
    const tagIds = selected.map((cb) => cb.value);

    try {
      const response = await fetch(this.renderUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken(),
          Accept: "text/html",
        },
        body: JSON.stringify({ tag_ids: tagIds }),
      });

      if (response.ok) {
        const html = await response.text();
        this.chipsTarget.innerHTML = html;
      } else {
        console.error("Failed to fetch rendered chips");
        // Fallback to client-side rendering
        this.renderChips();
      }
    } catch (error) {
      console.error("Error fetching rendered chips:", error);
      // Fallback to client-side rendering
      this.renderChips();
    }
  }

  renderChips() {
    const selected = this.checkboxTargets.filter((cb) => cb.checked);
    this.chipsTarget.innerHTML = "";

    selected.forEach((checkbox) => {
      const chip = this.createChip(
        checkbox.dataset.tagName || checkbox.dataset.userName || checkbox.value,
        checkbox.dataset.tagColor,
      );
      this.chipsTarget.appendChild(chip);
    });
  }

  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]');
    return token ? token.getAttribute("content") : "";
  }

  createChip(name, color) {
    const chip = document.createElement("span");
    chip.className =
      "tag-chip inline-flex items-center px-2 py-0.5 rounded-full text-xxs font-medium text-neutral-900 dark:text-white";

    if (color) {
      const hexColor = this.getColorValue(color);
      const bgColor = this.hexToRgba(hexColor, 0.2);
      const borderColor = this.hexToRgba(hexColor, 1.0);
      chip.style.backgroundColor = bgColor;
      chip.style.border = `1px solid ${borderColor}`;
    } else {
      // Default styling if no color provided
      chip.className += " bg-gray-100";
    }

    chip.textContent = name;
    return chip;
  }

  getColorValue(color) {
    if (!color) return "#6B7280"; // default gray
    if (color.startsWith("#")) return color;

    // Map common Tailwind colors to hex
    const colorMap = {
      "gray-500": "#6B7280",
      "red-500": "#EF4444",
      "blue-500": "#3B82F6",
      "green-500": "#10B981",
      "yellow-500": "#F59E0B",
      "purple-500": "#A855F7",
      "orange-500": "#F97316",
      "pink-500": "#EC4899",
      "indigo-500": "#6366F1",
      "teal-500": "#14B8A6",
    };

    return colorMap[color] || "#6B7280";
  }

  rgbFromColor(color) {
    if (color.startsWith("#")) {
      const hex = color.replace("#", "");
      return [
        parseInt(hex.substr(0, 2), 16),
        parseInt(hex.substr(2, 2), 16),
        parseInt(hex.substr(4, 2), 16),
      ];
    }
    // Parse from a Tailwind-like token via map
    const map = {
      "gray-500": "#6B7280",
      "red-500": "#EF4444",
      "blue-500": "#3B82F6",
      "green-500": "#10B981",
      "yellow-500": "#F59E0B",
      "purple-500": "#A855F7",
      "orange-500": "#F97316",
      "pink-500": "#EC4899",
      "indigo-500": "#6366F1",
      "teal-500": "#14B8A6",
    };
    const hex = map[color] || "#6B7280";
    return [
      parseInt(hex.substr(0, 2), 16),
      parseInt(hex.substr(2, 2), 16),
      parseInt(hex.substr(4, 2), 16),
    ];
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown();
    }
  }

  hexToRgba(hexColor, alpha) {
    const [r, g, b] = this.rgbFromColor(hexColor);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  handleKeydown(event) {
    // Handle Escape anywhere to close if open
    if (event.key === "Escape") {
      this.closeDropdown();
      return;
    }

    // Handle Enter on the button to open
    if (event.key === "Enter") {
      const active = document.activeElement;
      if (this.hasButtonTarget && active === this.buttonTarget) {
        event.preventDefault();
        this.toggle(event);
      }
    }
  }
}
